import { motion } from 'framer-motion'
import { Home, Search, ArrowLeft, Crop, Image, Scissors } from 'lucide-react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { useIntl } from 'react-intl'
import { useLanguage } from '../contexts/LanguageContext'

const NotFound = () => {
  const intl = useIntl()
  const { getLocalizedPath } = useLanguage()
  const t = (id: string) => intl.formatMessage({ id })
  const floatingIcons = [
    { icon: <Crop className="h-8 w-8" />, delay: 0 },
    { icon: <Image className="h-6 w-6" />, delay: 0.5 },
    { icon: <Scissors className="h-10 w-10" />, delay: 1 },
    { icon: <Crop className="h-7 w-7" />, delay: 1.5 },
    { icon: <Image className="h-9 w-9" />, delay: 2 },
  ]

  return (
    <>
      <SEO
        title={t('notFound.title')}
        description={t('notFound.description')}
        keywords={t('notFound.keywords')}
        noindex={true}
      />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen pt-20 relative overflow-hidden"
      >
        {/* Floating Background Icons */}
        <div className="absolute inset-0 pointer-events-none">
          {floatingIcons.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 100 }}
              animate={{ 
                opacity: [0, 0.1, 0],
                y: [-100, -200],
                x: [0, Math.random() * 100 - 50]
              }}
              transition={{
                duration: 4,
                delay: item.delay,
                repeat: Infinity,
                repeatDelay: 3
              }}
              className={`absolute text-primary-200 ${
                index === 0 ? 'top-1/4 left-1/4' :
                index === 1 ? 'top-1/3 right-1/4' :
                index === 2 ? 'top-1/2 left-1/6' :
                index === 3 ? 'top-2/3 right-1/6' :
                'top-3/4 left-1/2'
              }`}
            >
              {item.icon}
            </motion.div>
          ))}
        </div>

        {/* Main Content */}
        <section className="gradient-bg min-h-screen flex items-center justify-center relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* 404 Number */}
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
                className="mb-8"
              >
                <h1 className="text-8xl md:text-9xl font-display font-bold text-primary-600 mb-4 relative">
                  4
                  <motion.span
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    className="inline-block"
                  >
                    0
                  </motion.span>
                  4
                  
                  {/* Decorative crop icon in the middle zero */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 1 }}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  >
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                      <Crop className="h-8 w-8 text-primary-600" />
                    </div>
                  </motion.div>
                </h1>
              </motion.div>

              {/* Error Message */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mb-8"
              >
                <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
                  {t('notFound.mainTitle')}
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
                  {t('notFound.mainDescription')}
                </p>
                <p className="text-lg text-gray-500">
                  {t('notFound.subDescription')}
                </p>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
              >
                <Link
                  to={getLocalizedPath('/')}
                  className="btn-primary inline-flex items-center space-x-2 group"
                >
                  <Home className="h-5 w-5 group-hover:scale-110 transition-transform" />
                  <span>{t('notFound.buttons.backToHome')}</span>
                </Link>
                
                <Link
                  to={getLocalizedPath('/cropper')}
                  className="btn-secondary inline-flex items-center space-x-2 group"
                >
                  <Crop className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                  <span>{t('notFound.buttons.startCropping')}</span>
                </Link>
              </motion.div>

              {/* Quick Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="border-t border-gray-200 pt-8"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('notFound.quickLinks.title')}
                </h3>
                <div className="flex flex-wrap justify-center gap-6 text-sm">
                  <Link
                    to={getLocalizedPath('/')}
                    className="text-primary-600 hover:text-primary-700 transition-colors flex items-center space-x-1"
                  >
                    <Home className="h-4 w-4" />
                    <span>{t('notFound.quickLinks.home')}</span>
                  </Link>
                  <Link
                    to={getLocalizedPath('/faq')}
                    className="text-primary-600 hover:text-primary-700 transition-colors flex items-center space-x-1"
                  >
                    <Search className="h-4 w-4" />
                    <span>{t('notFound.quickLinks.faq')}</span>
                  </Link>
                  <Link
                    to={getLocalizedPath('/privacy')}
                    className="text-primary-600 hover:text-primary-700 transition-colors flex items-center space-x-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span>{t('notFound.quickLinks.privacy')}</span>
                  </Link>
                </div>
              </motion.div>

              {/* Fun Fact */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="mt-12 p-6 bg-white/50 backdrop-blur-sm rounded-lg border border-white/20"
              >
                <p className="text-gray-600 italic">
                  💡 <strong>{t('notFound.funFact.title')}</strong> {t('notFound.funFact.description')}
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-secondary-100 rounded-full opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 right-20 w-16 h-16 bg-accent-100 rounded-full opacity-20 animate-pulse delay-500"></div>
      </motion.div>
    </>
  )
}

export default NotFound